import { ModerationComment, PromptStatus } from '@ai-platform/utils';

import { StepStatus } from '../../components/UI/Steps/Steps.types';
import { Step } from '../../components/UI/Steps/Steps.types';
import { CONDITIONS_URL } from '../../constants';

/**
 * Массивы фраз для разных причин отклонения/отложения промпта
 */
const STATUS_MESSAGES = {
    // Фразы для статуса "Модерация"
    [PromptStatus.Moderation]: {
        default: [
            'Промпт ушёл на модерацию, но скоро вернётся.',
            'Ваш промпт стоит в очереди на проверку. Уже скоро его примем.',
            'Мы забрали ваш промпт! На проверку, конечно. Скоро вернём.',
            'Изучаем ваш промпт. Вернёмся в течение суток.',
        ],
    },
    // Фразы для статуса "Опубликован"
    [PromptStatus.Published]: {
        default: [
            'Ваш промпт уже на Промптхабе. Поздравляем!',
            'Ваш промпт добавлен на Промптхаб. Кажется, ему тут нравится!',
            'Классный промпт. Красивый такой. И людям нравится.',
            'С радостью сообщаем, что ваш промпт прошел нашу модерацию. Ура!',
        ],
    },
    // Фразы для статуса "Отклонен"
    [PromptStatus.Rejected]: {
        default: [
            `Очень жаль, но ваш промпт нарушает <a href='${CONDITIONS_URL}'>правила платформы</a>.`,
            `Мы пытались его спасти, но он нарушил <a href='${CONDITIONS_URL}'>правила платформы</a>.`,
            `Знаете главное правило Промптхаба? Не нарушать <a href='${CONDITIONS_URL}'>правила Промптхаба</a>. Кажется, ваш промпт об этом не знал.`,
            `Промптхаб и ваш промпт не могут быть вместе. Но дело не в промпте, а в <a href='${CONDITIONS_URL}'>правилах платформы</a>. `,
        ],
    },
    // Фразы для статуса "Отложен"
    [PromptStatus.Postponed]: {
        default: [
            'Этот промпт опередил своё время. Мы добавим его, когда на Промптхабе появятся нужные функции.',
            'Классный промпт, но на Промптхабе пока нет для него условий. Скоро доработаем платформу и добавим его.',
            'К сожалению, мы пока не можем добавить ваш промпт на Промптхаб. Скоро доработаем платформу и добавим его.',
        ],
    },
} as const;

/**
 * Возвращает случайный элемент из массива
 * @param array - массив элементов
 * @returns случайный элемент из массива
 */
const randomChoice = <T>(array: readonly T[]): T => {
    const randomIndex = Math.floor(Math.random() * array.length);

    return array[randomIndex];
};

/**
 * Возвращает случайную фразу в зависимости от статуса и комментария модератора
 * @param promptStatus - статус промпта
 * @param moderationComment - комментарий модератора (опционально)
 * @returns случайная фраза для InfoBlock
 */
export const getRandomPhrase = (promptStatus: PromptStatus, moderationComment?: ModerationComment): string => {
    const statusMessages = STATUS_MESSAGES[promptStatus];

    if (moderationComment && moderationComment in statusMessages) {
        return randomChoice(statusMessages[moderationComment] as string[]);
    }

    return randomChoice(statusMessages.default);
};

/**
 * Возвращает полный объект шага для третьего шага (публикация/отклонена) в зависимости от статуса промпта
 * @param promptStatus - статус промпта
 * @returns объект шага
 */
export const getPublicationStep = (promptStatus: PromptStatus | undefined): Step => {
    if (promptStatus === PromptStatus.Rejected) {
        return {
            text: 'Отклонен',
            status: StepStatus.Error,
            icon: 'close',
        };
    }
    if (promptStatus === PromptStatus.Postponed) {
        return {
            text: 'Отложен',
            status: StepStatus.Warning,
            icon: 'info',
        };
    }

    return {
        text: 'Публикация',
        status: StepStatus.Inactive,
        icon: 'tick',
    };
};

/**
 * Возвращает статус для второго шага (модерация) в зависимости от статуса промпта
 * @param promptStatus - статус промпта
 * @returns статус шага модерации
 */
export const getModerationStepStatus = (promptStatus: PromptStatus | undefined): StepStatus => {
    return promptStatus === PromptStatus.Rejected || promptStatus === PromptStatus.Postponed
        ? StepStatus.Done
        : StepStatus.Pending;
};
